import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQueryWithReauth } from 'utils/services';

import { GetBenefitChangeResProps, GetEntitlementsResProps } from './marketone.types';

/**
 * RTK Query API slice for the patient's MarketONE (Amdocs M1) reseller-partner entitlements —
 * the source for the AT&T appointment gating (LMD-1072), the AT&T benefit card on the billing
 * page (LMD-1095), and the unlinked-benefit landing page (LMD-1130).
 *
 * Failures deliberately reject rather than resolving to an empty payload: `useMarketOneEntitlement`
 * relies on RTK Query keeping the last successful payload so a failed background refetch cannot
 * revoke an already-confirmed benefit mid-flow. The global 500 toast is suppressed for this slice
 * in `store/index.ts` instead — see `SILENT_ERROR_REDUCER_PATHS` there.
 */
export const marketoneApi = createApi({
  baseQuery: baseQueryWithReauth,
  endpoints: (build) => ({
    getBenefitChange: build.query<GetBenefitChangeResProps, void>({
      keepUnusedDataFor: 0,
      providesTags: ['BenefitChange'],
      query: () => ({ url: '/marketone/benefit-change' })
    }),
    getEntitlements: build.query<GetEntitlementsResProps, void>({
      providesTags: ['Entitlements'],
      query: () => ({ url: '/marketone/entitlements' })
    })
  }),
  // 5-minute cache lifetime: a newly activated entitlement is visible on the next remount
  // rather than being served from a stale cache. Combined with refetchOnMountOrArgChange in
  // the consuming hook, this balances freshness against unnecessary network traffic.
  keepUnusedDataFor: 300,
  reducerPath: 'marketoneApi',
  tagTypes: ['BenefitChange', 'Entitlements']
});

export const { useGetBenefitChangeQuery, useGetEntitlementsQuery, useLazyGetEntitlementsQuery } =
  marketoneApi;
